export function formatBotResponseHTML(raw: string, isDisabled = false): string {
  const allowedTags = [
    "response",
    "tier1",
    "tier2",
    "related",
    "link",
    "ImageResponseCard",
    "button",
    "img",
    "a",
    "table",
    "thead",
    "tbody",
    "tr",
    "th",
    "td",
    "data_confidence",
    "methodology",
    "data_table",
    "calculation",
    "assessment",
    "patterns",
    "root_cause",
    "validation",
    "attribution",
    "actions",
    "action_context",
    "summary",
  ];

  const imageStyles = "margin-top:10px;margin-bottom:10px;width:100%";
  const hrStyle = "border:none;border-top:1px solid #334155;margin:16px 0;";
  const strongStyle = "font-weight:700";

  // Escape HTML to prevent XSS
  const escapeHTML = (str: string) =>
    str
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;");

  // Encode allowed tags temporarily
  let marked = raw.replace(
    new RegExp(`<\\/?(${allowedTags.join("|")})[^>]*>`, "gi"),
    (match) => `__HTMLTAG__${btoa(match)}__ENDTAG__`,
  );

  // Convert markdown-style images
  marked = marked.replace(
    /!\[([^\]]*)\]\(\s*<?([^)\s]+)>?(?:\s+["'][^"']*["'])?\s*\)/g,
    (_, alt, url) =>
      /^https?:/i.test(url)
        ? `<img src="${url}" alt="${escapeHTML(alt)}" style="${imageStyles}"/>`
        : _,
  );

  // Escape everything else
  let safe = escapeHTML(marked);

  // Basic markdown: headings, lists, hr, tables
  const lines = safe.split("\n");
  let inList = false;
  let tableLines: string[] = [];
  const parsed: string[] = [];

  const flushTable = () => {
    if (tableLines.length < 2) {
      // Not a valid table, just push lines as-is
      parsed.push(...tableLines);
      tableLines = [];
      return;
    }

    // Find the separator row (e.g., |---|---|)
    const separatorIdx = tableLines.findIndex((line) =>
      /^\|[\s\-:|]+\|$/.test(line.trim()),
    );

    if (separatorIdx === -1) {
      // No separator found, not a valid markdown table
      parsed.push(...tableLines);
      tableLines = [];
      return;
    }

    const headerRows = tableLines.slice(0, separatorIdx);
    const bodyRows = tableLines.slice(separatorIdx + 1);

    const parseRow = (row: string) =>
      row
        .split("|")
        .slice(1, -1)
        .map((cell) => cell.trim());

    let tableHTML =
      '<div style="overflow-x:auto;margin:12px 0;"><table class="output-table"><thead>';
    for (const hRow of headerRows) {
      const cells = parseRow(hRow);
      tableHTML +=
        "<tr>" + cells.map((c) => `<th>${c}</th>`).join("") + "</tr>";
    }
    tableHTML += "</thead><tbody>";
    for (const bRow of bodyRows) {
      const cells = parseRow(bRow);
      if (cells.length > 0 && cells.some((c) => c !== "")) {
        tableHTML +=
          "<tr>" + cells.map((c) => `<td>${c}</td>`).join("") + "</tr>";
      }
    }
    tableHTML += "</tbody></table></div>";

    parsed.push(tableHTML);
    tableLines = [];
  };

  let justFlushedTable = false;

  for (let line of lines) {
    const trimmed = line.trim();

    // Detect table rows (lines starting and ending with |)
    if (/^\|.*\|$/.test(trimmed)) {
      if (inList) {
        parsed.push("</ul>");
        inList = false;
      }
      tableLines.push(trimmed);
      justFlushedTable = false;
      continue;
    }

    // If we were collecting table lines but this line isn't a table row, flush
    if (tableLines.length > 0) {
      flushTable();
      justFlushedTable = true;
    }

    // Skip empty lines immediately after a table to avoid duplicate spacing
    if (justFlushedTable && trimmed === "") {
      continue;
    }
    justFlushedTable = false;

    const headingMatch = trimmed.match(/^(#{1,6})\s+(.*)$/);
    if (headingMatch) {
      if (inList) {
        parsed.push("</ul>");
        inList = false;
      }
      const level = headingMatch[1].length;
      parsed.push(
        `<h${level} style="color:#fff;">${headingMatch[2]}</h${level}>`,
      );
      continue;
    }

    if (trimmed.startsWith("- ")) {
      if (!inList) parsed.push("<ul>");
      inList = true;
      parsed.push(`<li>${trimmed.substring(2)}</li>`);
      continue;
    }

    if (/^[-]{3,}$/.test(trimmed)) {
      if (inList) {
        parsed.push("</ul>");
        inList = false;
      }
      parsed.push(`<hr style="${hrStyle}"/>`);
      continue;
    }

    if (inList && trimmed === "") {
      parsed.push("</ul>");
      inList = false;
    }

    if (trimmed === "") {
      parsed.push(`
      <div style="height: 15px; border-radius:4px;"></div>
      `);
      continue;
    }

    parsed.push(trimmed);
  }

  // Flush any remaining table or list
  if (tableLines.length > 0) flushTable();
  if (inList) parsed.push("</ul>");
  safe = parsed.join("\n");

  // Bold text
  safe = safe.replace(
    /\*\*(.+?)\*\*/g,
    `<strong style="${strongStyle}">$1</strong>`,
  );

  // Linkify URLs
  const urlRegex = /(https?:\/\/[^\s<]+)/g;
  safe = safe
    .split(/(<[^>]+>)/g)
    .map((chunk) =>
      chunk.startsWith("<")
        ? chunk
        : chunk.replace(
            urlRegex,
            '<a href="$1" target="_blank" rel="noopener noreferrer">$1</a>',
          ),
    )
    .join("");

  // Decode allowed tags
  const tier2Sections = [
    "data_confidence",
    "methodology",
    "data_table",
    "calculation",
    "assessment",
    "patterns",
    "root_cause",
    "validation",
    "attribution",
    "actions",
    "action_context",
    "summary",
  ];

  safe = safe.replace(/__HTMLTAG__(.*?)__ENDTAG__/g, (_, encoded) => {
    const decoded = atob(encoded).trim();

    /* ---------- RESPONSE ---------- */
    if (decoded === "<response>") {
      return `<div class="iris-response" style="
      border-radius:12px;
      color:#e5e7eb;
      line-height:1.6;
    ">`;
    }
    if (decoded === "</response>") return `</div>`;

    /* ---------- TIER 1 ---------- */
    if (decoded === "<tier1>") {
      return `<div class="iris-tier1" style="font-size:15px;margin-bottom:16px;">`;
    }
    if (decoded === "</tier1>") return `</div>`;

    /* ---------- TIER 2 ---------- */
    if (decoded === "<tier2>") {
      return `<div class="iris-tier2" style="
        color:#fff;
        font-size:15px;
        margin-bottom:16px;
        border-radius:8px;
      ">`;
    }
    if (decoded === "</tier2>") return `</div>`;

    /* ---------- TIER2 COLLAPSIBLE SECTIONS ---------- */
    if (tier2Sections.includes(decoded.replace(/[<>]/g, ""))) {
      const title = decoded
        .replace("<", "")
        .replace(">", "")
        .replace(/_/g, " ")
        .replace(/\b\w/g, (c) => c.toUpperCase());

      return `
      <div class="iris-tier2-section">
        <div class="iris-tier2-header" style="cursor:pointer;font-weight:700;margin-bottom:8px;">
        <label>${title}</label><i class="fas fa-chevron-down"></i>
        </div>
        <div class="iris-tier2-content hidden">
    `;
    }
    if (tier2Sections.includes(decoded.replace(/[<>]/g, "").replace("/", ""))) {
      return `</div></div>`;
    }

    /* ---------- RELATED ---------- */
    if (decoded === "<related>") {
      return `<div class="iris-related" style="margin-top:12px;padding-top:20px;border-top:1px solid #334155;font-size:13px;opacity:0.9;">`;
    }
    if (decoded === "</related>") return `</div>`;

    /* ---------- LINK ---------- */
    if (decoded.startsWith("<link")) {
      return `<button class="iris-button-class" ${
        isDisabled ? "disabled style='opacity:0.5;cursor:not-allowed;'" : ""
      }>`;
    }
    if (decoded === "</link>") return `</button>`;

    /* ---------- IMAGE RESPONSE CARD ---------- */
    if (decoded === "<ImageResponseCard>")
      return `<div class="iris-button-class-container--blk" style="margin-bottom:20px">`;
    if (decoded === "</ImageResponseCard>") return `</div>`;

    /* ---------- BUTTON ---------- */
    if (decoded.startsWith("<button")) {
      return decoded.replace(
        /<button(.*?)>/g,
        `<button class="iris-button-class"$1 ${
          isDisabled ? "disabled style='opacity:0.5;cursor:not-allowed;'" : ""
        }>`,
      );
    }

    return decoded;
  });

  // Remove any inline JS to be safe
  safe = safe.replace(/onclick="[^"]*"/gi, "");

  return safe;
}
